import { useEffect, useMemo, useRef, useState } from 'react';
import './CloudCapacityManagement.less';
import { Button, Tabs, TabsProps, Tooltip, Typography } from 'antd';
import { FormattedMessage } from 'react-intl';
import { PlusOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import CloudProviderTab from './CloudProviderTab/CloudProviderTab';
import { useGetCloudProviders } from 'hooks/capacityManagementQueries';
import { useGetPlatforms } from 'hooks/platformActionsQueries';
import { InstancePlatform } from 'types/cluster';
import { useAccess } from 'hooks/useAccess';

function CloudCapacityManagement() {
  const { Title } = Typography;
  const [searchParams, setSearchParams] = useSearchParams();

  const { can } = useAccess();
  const canEditSizes = can('sizes.update');

  const { data: cloudProviders, isLoading: isCloudProvidersLoading } =
    useGetCloudProviders();
  const { data: platforms, isLoading: isPlatformsLoading } = useGetPlatforms();

  const [showModalForNewSize, setShowModalForNewSize] = useState(false);
  const [showCellEditModal, setShowCellEditModal] = useState(false);
  const [clickedCellValue, setClickedCellValue] = useState<any>();
  const [prefillCellData, setPrefillCellData] = useState<any>();

  const requestedPlatformKey = searchParams.get('platform') || undefined;
  const requestedOperator = searchParams.get('operator') || undefined;
  const requestedSizeName = searchParams.get('sizeName') || undefined;

  const initialAdvancedSearchValue = useMemo(() => {
    const filters: string[] = [];

    if (requestedOperator) {
      filters.push(`'operatorClusterCode'=${requestedOperator}`);
    }

    if (requestedSizeName) {
      filters.push(`'sizeName'=${requestedSizeName}`);
    }

    return filters.join('&&');
  }, [requestedOperator, requestedSizeName]);

  const cloudProviderIdsByKey = useMemo(
    () =>
      new Map(
        (cloudProviders ?? []).map((cloudProvider) => [
          cloudProvider.key,
          cloudProvider.id,
        ]),
      ),
    [cloudProviders],
  );

  const tabsItems: TabsProps['items'] = platforms
    ?.filter(
      (platform) =>
        ![InstancePlatform.AZURE].includes(platform.code as InstancePlatform) &&
        cloudProviderIdsByKey.has(platform.code),
    )
    ?.map((platform) => {
      const cloudProviderID = cloudProviderIdsByKey.get(platform.code);

      if (cloudProviderID === undefined) {
        return null;
      }

      return {
        key: platform.code,
        label: (
          <span className="cloud-capacity-management__tab-label">
            <span
              className={`cloud-capacity-management__tab-icon cloud-capacity-management__tab-icon--${platform.code.toLowerCase()}`}
            >
              <img src={platform.image} alt={platform.displayName} />
            </span>
            <span className="cloud-capacity-management__tab-text">
              {platform.displayName}
            </span>
          </span>
        ),

        children: (
          <CloudProviderTab
            cloudProvider={{
              id: cloudProviderID,
              key: platform.code,
              displayName: platform.displayName,
              createdAt: '',
              updatedAt: '',
            }}
            showModalForNewSize={showModalForNewSize}
            setShowModalForNewSize={setShowModalForNewSize}
            initialAdvancedSearchValue={initialAdvancedSearchValue}
            showCellEditModal={showCellEditModal}
            setShowCellEditModal={setShowCellEditModal}
            clickedCellValue={clickedCellValue}
            setClickedCellValue={setClickedCellValue}
            prefillCellData={prefillCellData}
            setPrefillCellData={setPrefillCellData}
            canEdit={canEditSizes}
          />
        ),
      };
    })
    .filter(Boolean);

  // The active tab is URL-driven (the `platform` param), so switching tabs is just
  // a URL change — no local state to fight with the deep-link value.
  const activeTabKey =
    requestedPlatformKey && tabsItems?.some((item) => item?.key === requestedPlatformKey)
      ? requestedPlatformKey
      : String(
          tabsItems?.find((item) => item?.key === InstancePlatform.GC)?.key ||
            tabsItems?.[0]?.key ||
            '',
        );

  const handleTabChange = (key: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('platform', key);
        // Deep-link filter params are per-tab and one-shot — drop them on switch.
        next.delete('operator');
        next.delete('sizeName');
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    setShowCellEditModal(false);
    setClickedCellValue(undefined);
    setPrefillCellData(undefined);
    setShowModalForNewSize(false);
  }, [activeTabKey]);

  return (
    <section className="cloud-capacity-management__wrapper">
      <div className="tableTitle">
        <Title>
          <FormattedMessage id="cloudCapacityManagement.pageTitle" />
        </Title>
        <Tooltip
          title={
            canEditSizes
              ? 'Create'
              : 'You don❜t have sufficient permission'
          }
        >
          <Button
            type="primary"
            icon={<PlusOutlined className='icon-margin'/>}
            onClick={() => {
              setShowModalForNewSize(true);
            }}
            disabled={
              isCloudProvidersLoading ||
              isPlatformsLoading ||
              !canEditSizes
            }
          >
            <FormattedMessage id="cloudCapacityManagement.createNewSizeButton" />
          </Button>
        </Tooltip>
      </div>
      <Tabs
        centered
        items={tabsItems}
        activeKey={activeTabKey}
        onChange={handleTabChange}
        destroyInactiveTabPane
        className="cloud-capacity-management__tabs"
      />
    </section>
  );
}

export default CloudCapacityManagement;
